import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontHeebo, fontMerriweather, fontPlayfairDisplay, fontRoboto } from "@product/scmp-sdk";

import { ArticleHeaderAdSlot } from "~/components/article/article-header-ad-slot";
import { ArticleContentRightSection } from "~/components/article/article-render/article-content/article-content-right-section";
import { pageStyle } from "~/components/article/article-render/article-content/standard-styles";
import { StyledAdSlot, StyledContentHeadline } from "~/components/article/article-render/styles";
import { StyledLi, StyledUl } from "~/components/content/content-schema-render/styles";
import { section } from "~/data";
import type { WithStickyStackedHeightProps } from "~/lib/styles";
import { setFullContentWidthStyles, setFullViewportWidthStyles } from "~/lib/styles";

export const GenericArticleContainer = styled.div`
  display: grid;
  grid:
    "leading" min-content
    "section" min-content
    "headline" min-content
    "sub-headline" min-content
    "sponsor" min-content
    "follow" min-content
    "author" min-content
    "mobile-content-header-ad" min-content
    "correction" min-content
    "speech-player" min-content
    "content" min-content
    "mobile-content-footer-ad" min-content
    "comment" min-content
    "more-on-this" min-content
    "author-detail" min-content
    "topic" min-content
    "mobile-footer-ad" min-content
    / 100%;

  inline-size: 100%;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      ". . section" min-content
      ". . headline" min-content
      ". . sub-headline" min-content
      ". . sponsor" min-content
      ". . follow" min-content
      ". . author" min-content
      "left . leading" min-content
      "left . correction" min-content
      "left . speech-player" min-content
      "left . content" min-content
      "left . tablet-content-footer-ad" min-content
      "left . comment" min-content
      "left . more-on-this" min-content
      "left . author-detail" min-content
      "left . topic" min-content
      "left . ." min-content
      / 44px 20px minmax(0, 1fr);
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "header-ad header-ad header-ad header-ad header-ad" min-content
      ". . section section section" min-content
      ". . headline headline headline" min-content
      ". . sub-headline sub-headline sub-headline" min-content
      ". . sponsor sponsor sponsor" min-content
      ". . follow follow follow" min-content
      ". . author author author" min-content
      "left . speech-player speech-player speech-player" min-content
      "left . leading leading leading" min-content
      "left . correction correction correction" min-content
      "left . content . right" min-content
      "left . comment . right" min-content
      "left . author-detail . right" min-content
      "left . topic . right" min-content
      "left . . . right" min-content
      / 44px 20px minmax(0, 1fr) 40px 300px;
  }

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    grid:
      "header-ad header-ad header-ad header-ad header-ad header-ad header-ad" min-content
      ". . . . section section section" min-content
      "widgets-top . . . headline headline headline" min-content
      "widgets-top . . . sub-headline sub-headline sub-headline" min-content
      "widgets-top . . . sponsor sponsor sponsor" min-content
      "widgets-top . . . author author author" min-content
      "widgets-bottom . left . speech-player speech-player speech-player" min-content
      "widgets-bottom . left . leading leading leading" min-content
      "widgets-bottom . left . correction correction correction" min-content
      "widgets-bottom . left . content . right" min-content
      "widgets-bottom . left . comment . right" min-content
      "widgets-bottom . left . author-detail . right" min-content
      "widgets-bottom . left . topic . right" min-content
      "widgets-bottom . left . . . right" min-content
      / 180px 40px 44px 40px minmax(0, 1fr) 40px 300px;
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const Left = styled.div<WithStickyStackedHeightProps>`
  grid-area: left;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 50}px;

  display: none;
  align-self: flex-start;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;
    flex-flow: column nowrap;
    gap: 60px;
    align-items: center;

    margin-block-start: 40px;
  }
`;

export const RightAvailableSpaceTracker = styled.div`
  grid-row: content-start / topic-end;
  grid-column: span 1;

  display: none;

  inline-size: 0;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;

const Widgets = styled.div`
  display: none;

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    display: flex;
  }
`;
export const WidgetsTop = styled(Widgets)`
  grid-area: widgets-top;

  flex-flow: column nowrap;
  gap: 24px;
`;

export const WidgetsBottom = styled(Widgets)`
  grid-area: widgets-bottom;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
  }
`;

export const WidgetsBottomAvailableSpaceTracker = styled(Widgets)`
  grid-row: leading-start / topic-end;
  grid-column: span 1;

  inline-size: 0;
`;

export const Leading = styled.div`
  grid-area: leading;

  ${props => props.theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthStyles()}
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
  }
`;

export const Section = styled.div`
  grid-area: section;

  position: relative;
  z-index: 1;

  margin-block-start: -10px;
  padding-block-start: 12px;
  padding-inline: 12px;

  background: #ffffff;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
    padding-block-start: 0;
    padding-inline: 0;
  }
`;

type MagazineVariant =
  | typeof section.postMagazine.entityId
  | typeof section.thisWeekInAsia.entityId;
type HeadlineProps = {
  $magazineVariant?: MagazineVariant;
};
export const Headline = styled.div<HeadlineProps>`
  grid-area: headline;

  margin-block-start: 8px;
  padding-inline: 12px;

  font-weight: 900;
  font-size: 24px;
  font-family: ${fontMerriweather};
  line-height: 33px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
    padding-inline: 0;

    font-size: 34px;
    line-height: 46px;
  }

  ${props => {
    switch (props.$magazineVariant) {
      case section.postMagazine.entityId:
        return css`
          margin-block-start: 5px;
          ${props.theme.breakpoints.up("tablet")} {
            margin-block-start: 10px;
          }

          ${StyledContentHeadline} {
            font-weight: 900;
            font-size: 26px;
            font-family: ${fontPlayfairDisplay};
            line-height: 33px;

            ${props.theme.breakpoints.up("tablet")} {
              font-size: 38px;
              line-height: 51px;
            }
          }
        `;
      case section.thisWeekInAsia.entityId:
        return css`
          margin-block-start: 10px;
          ${props.theme.breakpoints.up("tablet")} {
            margin-block-start: 14px;
          }

          ${StyledContentHeadline} {
            font-weight: 800;
            font-size: 26px;
            font-family: ${fontHeebo};
            line-height: 33px;

            ${props.theme.breakpoints.up("tablet")} {
              font-size: 40px;
              line-height: 48px;
            }
          }
        `;
    }
  }}
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  margin-block-start: 12px;
  padding-inline: 12px;

  font-weight: 300;
  font-size: 19px;
  font-family: ${fontRoboto};
  line-height: 23px;

  ${StyledLi} + ${StyledLi} {
    margin-block-start: 10px;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
    padding-inline: 0;

    font-size: 24px;
    line-height: 32px;

    ${StyledUl} {
      padding-inline-start: 18px;
    }

    ${StyledLi} + ${StyledLi} {
      margin-block-start: 12px;
    }
  }
`;

export const Sponsor = styled.div`
  grid-area: sponsor;

  margin-block-start: 12px;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;
  }
`;

export const Follow = styled.div`
  grid-area: follow;

  margin-block-start: 20px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
  }

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    display: none;
  }
`;

export const Author = styled.div`
  grid-area: author;

  margin-block-start: 20px;
  padding-inline: 12px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
    padding-inline: 0;
  }
`;

export const Content = styled.div`
  grid-area: content;

  margin-block-start: 32px;
  ${props => pageStyle(props)}
`;

export const CommentAndReaction = styled.div`
  grid-area: comment;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const Correction = styled.div`
  grid-area: correction;

  margin-block-start: 32px;

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  ${props => props.theme.breakpoints.only("mobile")} {
    margin-block-start: 32px;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block: 32px;
    padding-inline: 20px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  margin-block-start: 20px;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
  }
`;

const MobileAdSlot = styled.div`
  ${setFullViewportWidthStyles()}

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const MobileContentHeaderAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-content-header-ad;

  margin-block-start: 20px;
  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const MobileContentFooterAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-content-footer-ad;
`;

export const MobileFooterAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-footer-ad;

  margin-block-start: 32px;

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

const TabletAdSlot = styled.div`
  display: none;

  ${props => props.theme.breakpoints.only("tablet")} {
    display: block;
  }
`;

export const TabletContentFooterAdSlot = styled(TabletAdSlot)`
  grid-area: tablet-content-footer-ad;
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }

  ${setFullContentWidthStyles()}
`;

export const TopPicksAdSlot = styled.div``;

export const StyledArticleContentRightSection = styled(ArticleContentRightSection)`
  grid-area: right;
  ${props => props.theme.breakpoints.up("desktop")} {
    margin-block-start: 40px;
  }
`;

export const SpeechPlayer = styled.div`
  grid-area: speech-player;
`;
